// Focused financial planning and history pages reuse advisor generation, plan storage and query caches; asset setup belongs to the household area.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { History, Loader2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useWorkspaceStore } from '@/stores/workspace';
import { useMaskAmounts } from '@/hooks/useMaskAmounts';
import { PageLayout, PageLink } from '@/components/PageLayout';
import { HubTile } from '@/components/HubTile';
import { usePlanStore } from '@/stores/plan';
import { useLatestPlan, type PlanSession } from '@/hooks/useLatestPlan';
import { motion } from 'framer-motion';
import { staggerList, staggerItem } from '@/lib/motion';
import { CoachCard } from '@/components/insights/CoachCard';
import { MaskToggle } from '@/components/MaskToggle';

interface SessionListItem {
  id: string;
  generated_at: number;
  model: string;
  language: string;
  assessment: string;
}

const PLAN_TILES = [
  {
    id: 'assessment',
    icon: '📝',
    to: '/assistant/plans/assessment',
    titleKey: 'advisor.plan.subpage.assessmentTitle',
    descKey: 'advisor.plan.subpage.assessmentDesc',
  },
  {
    id: 'budget-alerts',
    icon: '⚠️',
    to: '/assistant/plans/budget-alerts',
    titleKey: 'advisor.plan.subpage.budgetAlertsTitle',
    descKey: 'advisor.plan.subpage.budgetAlertsDesc',
  },
  {
    id: 'cashflow',
    icon: '💵',
    to: '/assistant/plans/cashflow',
    titleKey: 'advisor.plan.subpage.cashflowTitle',
    descKey: 'advisor.plan.subpage.cashflowDesc',
  },
  {
    id: 'recommendations',
    icon: '💡',
    to: '/assistant/plans/recommendations',
    titleKey: 'advisor.plan.subpage.recommendationsTitle',
    descKey: 'advisor.plan.subpage.recommendationsDesc',
  },
  {
    id: 'goals',
    icon: '🎯',
    to: '/assistant/plans/goals',
    titleKey: 'advisor.plan.subpage.goalsTitle',
    descKey: 'advisor.plan.subpage.goalsDesc',
  },
  {
    id: 'risks',
    icon: '🚨',
    to: '/assistant/plans/risks',
    titleKey: 'advisor.plan.subpage.risksTitle',
    descKey: 'advisor.plan.subpage.risksDesc',
  },
];

export function AdvisorPage({ mode = 'plan' }: { mode?: 'plan' | 'history' }) {
  const { t, i18n } = useTranslation();
  const zh = !i18n.language.startsWith('en');
  const ws = useWorkspaceStore((s) => s.current);
  const { masked, toggle } = useMaskAmounts();
  const sessionsQuery = useQuery({
    queryKey: ['advisor', 'sessions', ws?.id],
    queryFn: () => api.get<SessionListItem[]>('/advisor/sessions?limit=10'),
    enabled: !!ws && mode === 'history',
  });
  const planQuery = useLatestPlan();
  const plan = planQuery.data;
  const setCurrentPlanId = usePlanStore((s) => s.setCurrentPlanId);
  const [planError, setPlanError] = useState<string | null>(null);
  const [lookbackMonths, setLookbackMonths] = useState(3);
  const [elapsed, setElapsed] = useState(0);
  const generateMutation = useMutation({
    mutationFn: () => api.post<PlanSession>('/advisor/generate', { language: 'zh', lookback_months: lookbackMonths }),
    onSuccess: (data) => {
      setCurrentPlanId(data.id);
      setPlanError(null);
    },
    onError: (err: unknown) => setPlanError(err instanceof ApiError ? err.message : 'failed'),
  });
  useEffect(() => {
    if (!generateMutation.isPending) { setElapsed(0); return; }
    const startedAt = Date.now();
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [generateMutation.isPending]);
  return (
    <PageLayout title={mode === 'history' ? (zh ? '规划历史' : 'Planning history') : (zh ? '财务规划' : 'Financial planning')} backTo={mode === 'history' ? '/assistant/plans' : '/assistant'} actions={<MaskToggle masked={masked} onClick={toggle} />} wide>
      {mode === 'history' ? (
        sessionsQuery.isPending ? <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />{t('common.loading')}</p> :
        sessionsQuery.error ? <p role="alert" className="text-sm text-destructive">{sessionsQuery.error.message}<button className="ml-2 underline" onClick={() => sessionsQuery.refetch()}>{zh ? '重试' : 'Retry'}</button></p> :
        <SessionsCard sessions={sessionsQuery.data ?? []} />
      ) : (
        <>
          <PageLink to="/assistant/history" icon={History} title={zh ? '规划历史' : 'Planning history'} description={zh ? '查看之前生成的完整报告' : 'Review previously generated reports'} />
          <div className="space-y-5">
            {planQuery.stalePlan && <p role="status" className="rounded-xl border bg-card p-3 text-sm text-amber-700 dark:text-amber-300">{t('money.planStale', { defaultValue: '账单金额已更新，原计划保留在历史中。请重新生成计划，再使用其中的建议。' })}</p>}
            <section className="space-y-2">
              <div>
                <h2 className="text-lg font-semibold">{t('advisor.plan.quickHeader')}</h2>
                <p className="text-xs text-muted-foreground">
                  {t('advisor.plan.quickSubtitle')}
                </p>
              </div>
              <CoachCard currency={ws?.default_currency ?? 'AUD'} />
            </section>

            <section className="space-y-3">
              <div>
                <h2 className="text-lg font-semibold">{t('advisor.plan.fullHeader')}</h2>
                <p className="text-xs text-muted-foreground">
                  {t('advisor.plan.fullSubtitle')}
                </p>
              </div>
            <div className="rounded-2xl border bg-card p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="text-sm">
                  <span className="text-muted-foreground">{t('advisor.lookbackLabel')}</span>
                  <select
                    value={lookbackMonths}
                    onChange={(e) => setLookbackMonths(Number(e.target.value))}
                    className="ml-2 rounded-md border bg-background px-2 py-1 text-sm"
                  >
                    {[1, 3, 6, 12].map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </label>
                <button
                  onClick={() => generateMutation.mutate()}
                  disabled={generateMutation.isPending}
                  className="inline-flex min-h-[44px] items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
                >
                  {generateMutation.isPending ? <><Loader2 className="h-4 w-4 animate-spin" />{zh ? '正在生成' : 'Generating'} · {elapsed}s</> : t('advisor.generate')}
                </button>
              </div>
              {planError && (
                <div className="mt-2 rounded-md border border-rose-300 bg-rose-50 p-2 text-sm text-rose-800">
                  {planError}
                </div>
              )}
              {plan && (
                <div className="mt-2 text-xs text-muted-foreground">
                  {t('advisor.plan.generatedAt', {
                    when: new Date(plan.generated_at * 1000).toLocaleString(),
                  })}
                </div>
              )}
            </div>

            {!plan && !generateMutation.isPending && (
              <div className="rounded-2xl border bg-card p-6 text-center text-sm text-muted-foreground">
                {t('advisor.planEmpty')}
              </div>
            )}

            {plan && (
              <motion.div
                className="space-y-3"
                variants={staggerList}
                initial="initial"
                animate="animate"
              >
                {PLAN_TILES.map((tile) => {
                  const meta = planTileMeta(tile.id, plan);
                  return (
                    <motion.div key={tile.id} variants={staggerItem}>
                      <HubTile
                        to={tile.to}
                        icon={tile.icon}
                        title={t(tile.titleKey)}
                        description={meta ?? t(tile.descKey)}
                      />
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
            </section>
          </div>
        </>
      )}
    </PageLayout>
  );
}
// One-line summary surface for each plan tile — first line of assessment,
// alert counts, etc. Falls back to the generic description if empty.
function planTileMeta(id: string, plan: PlanSession): string | null {
  switch (id) {
    case 'assessment':
      return plan.assessment.split('\n').find((l) => l.trim().length > 0)?.slice(0, 80) ?? null;
    case 'budget-alerts':
      return plan.budget_alerts.length > 0
        ? `${plan.budget_alerts.length} 项`
        : null;
    case 'cashflow':
      return plan.cashflow_alerts.length > 0
        ? `${plan.cashflow_alerts.length} 项`
        : null;
    case 'recommendations':
      return plan.recommendations.length > 0
        ? `${plan.recommendations.length} 条建议`
        : null;
    case 'goals':
      return plan.goal_projections.length > 0
        ? `${plan.goal_projections.length} 项`
        : null;
    case 'risks':
      return plan.risks.length > 0 ? `${plan.risks.length} 项` : null;
    default:
      return null;
  }
}

function SessionsCard(props: { sessions: SessionListItem[] }) {
  const { t } = useTranslation();
  if (props.sessions.length === 0) {
    return (
      <div className="rounded-2xl border bg-card p-6 text-center text-sm text-muted-foreground">
        {t('advisor.historyEmpty')}
      </div>
    );
  }
  return (
    <div className="rounded-2xl border bg-card p-4">
      <div className="mb-2 text-sm font-medium">{t('advisor.historyTitle')}</div>
      <div className="space-y-2">
        {props.sessions.map((s) => {
          const dt = new Date(s.generated_at * 1000);
          return (
            <Link
              key={s.id}
              to={`/assistant/history/${s.id}`}
              className="block rounded-xl border p-3 text-sm transition hover:bg-muted/50"
            >
              <div className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
                <span>{dt.toLocaleString()}</span>
                <span>{s.model}</span>
              </div>
              <p className="mt-1 line-clamp-3 text-sm">{s.assessment}</p>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
